import { useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type ProfileType = 'Owner' | 'Driver';

export const useProfile = (userGroups: string[] = []) => {
  const [selectedProfile, setSelectedProfile] = useState<ProfileType>('Owner');
  const [isLoading, setIsLoading] = useState(true);

  const hasOwner = userGroups.includes('Owner');
  const hasDriver = userGroups.includes('Driver');

  // Determine effective profile based on user roles and selected profile
  const effectiveProfile: ProfileType = hasDriver && !hasOwner
    ? 'Driver'
    : selectedProfile;

  // Load saved profile from storage on mount
  useEffect(() => {
    const loadSavedProfile = async () => {
      try {
        const savedProfile = await AsyncStorage.getItem('selectedProfile');
        if (savedProfile && (savedProfile === 'Owner' || savedProfile === 'Driver')) {
          // Only set the saved profile if the user has that role
          if ((savedProfile === 'Owner' && hasOwner) || (savedProfile === 'Driver' && hasDriver)) {
            setSelectedProfile(savedProfile);
          }
        }
      } catch (error) {
        console.error('Error loading saved profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedProfile();
  }, [hasOwner, hasDriver]);

  // Save profile to storage whenever it changes
  const updateProfile = async (newProfile: ProfileType) => {
    try {
      await AsyncStorage.setItem('selectedProfile', newProfile);
      setSelectedProfile(newProfile);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  return {
    selectedProfile,
    effectiveProfile,
    isLoading,
    updateProfile,
    hasOwner,
    hasDriver,
  };
};
